import ActionBase, { ACTION_TYPES } from './ActionBase.js';

/**
 * @module Core/data/stm/action/InsertChildAction
 */

/**
 * Action to record the fact of adding children models into a parent model.
 * @extends Core/data/stm/action/ActionBase
 */
export default class InsertChildAction extends ActionBase {
    static $name = 'InsertChildAction';

    static configurable = {
        /**
         * Reference to a parent model a child model has been added to.
         *
         * @prp {Core.data.Model}
         * @readonly
         * @default
         */
        parentModel : undefined,

        /**
         * Children models inserted.
         *
         * @prp {Core.data.Model[]}
         * @readonly
         * @default
         */
        childModels : undefined,

        /**
         * Index a children models are inserted at
         *
         * @prp {Number}
         * @readonly
         * @default
         */
        insertIndex : undefined,

        /**
         * Reference node in the ordered tree
         *
         * @prp {Core.data.Model}
         * @readonly
         * @default
         * @internal
         */
        orderedBeforeNode : undefined,

        /**
         * Map having children models as keys and values containing previous parent
         * of each model and index at the previous parent.
         *
         * @prp {Object}
         * @readonly
         * @default
         */
        context : undefined,

        stores : undefined
    };

    get type() {
        return ACTION_TYPES.INSERT_CHILD;
    }

    //<debug>
    afterConfig() {
        super.afterConfig();

        console.assert(
            this.parentModel.isModel &&
            Array.isArray(this.childModels) &&
            this.childModels.every(m => m.isModel) &&
            this.insertIndex !== undefined &&
            this.context instanceof Map &&
            Array.isArray(this.stores),
            "Can't create action, bad configuration!"
        );
    }
    //</debug>

    changeParentModel(model) {
        //<debug>
        console.assert(
            !this._parentModel && model,
            "Can't set parent model, model is required and can be set only once!"
        );
        //</debug>

        return model;
    }

    changeChildModels(models) {
        //<debug>
        console.assert(
            !this._childModels &&
            Array.isArray(models) &&
            models.every(m => m.isModel),
            "Can't set child models, models are required, it should be array of Model class and can be set only once!"
        );
        //</debug>

        return models.slice(0);
    }

    changeInsertIndex(index) {
        //<debug>
        console.assert(
            this._insertIndex === undefined && index !== undefined,
            "Can't set insert index, the value is required and can be set only once!"
        );
        //</debug>

        return index;
    }

    changeContext(ctx) {
        //<debug>
        console.assert(
            !this._context &&
            ctx instanceof Map &&
            Array.from(ctx.entries()).every(([k, v]) => {
                return k.isModel && typeof v == 'object' && v && Object.prototype.hasOwnProperty.call(v, 'parent') && Object.prototype.hasOwnProperty.call(v, 'index');
            }),
            "Can't set context, the value is required it should be Map keyed by inserted models with object values containing `parent` and `index` properties, and it can be set only once!"
        );
        //</debug>

        return ctx;
    }

    changeStores(stores) {
        //<debug>
        console.assert(Array.isArray(stores) && stores.every(s => s.isStore), 'Stores should be an array of store instances');
        //</debug>

        return stores;
    }

    undo() {
        const
            { parentModel, context, childModels } = this,
            byFromParent = new Map(),
            newlyAdded = new Set();

        for (const childModel of childModels) {
            const ctx = context.get(childModel);

            if (!ctx) {
                newlyAdded.add(childModel);
            }
            else {
                let undoTaskData = byFromParent.get(ctx.parent);

                if (!undoTaskData) {
                    undoTaskData = { moveRight : [], moveLeft : [], moveFromAnotherParent : [] };
                    byFromParent.set(ctx.parent, undoTaskData);
                }

                if (ctx.parent === parentModel) {
                    if (ctx.index > childModel.parentIndex) {
                        undoTaskData.moveRight.push({ parent : ctx.parent, model : childModel, index : ctx.index + 1 });
                    }
                    else {
                        undoTaskData.moveLeft.push({ parent : ctx.parent, model : childModel, index : ctx.index });
                    }
                }
                else {
                    undoTaskData.moveFromAnotherParent.push({ parent : ctx.parent, model : childModel, index : ctx.index });
                }
            }
        }

        newlyAdded.forEach(model => model.parent?.removeChild(model));

        // first re-insert the nodes, that have been moved from another parent nodes
        // probably also need some kind of sorting, like starting from depth-most ones
        for (const undoTaskData of byFromParent.values()) {
            const { moveRight, moveLeft, moveFromAnotherParent } = undoTaskData;

            moveFromAnotherParent.forEach(task => {
                task.parent.insertChild(task.model, task.index);
            });

            moveLeft.sort((a, b) => a.index - b.index);
            moveRight.sort((a, b) => b.index - a.index);
        }

        // and after then move the nodes left/right
        for (const undoTaskData of byFromParent.values()) {
            const { moveRight, moveLeft } = undoTaskData;

            moveLeft.forEach(task => {
                task.parent.insertChild(task.model, task.index);
            });
            moveRight.forEach(task => {
                task.parent.insertChild(task.model, task.index);
            });
        }
    }

    redo() {
        const
            { parentModel, insertIndex, childModels, orderedBeforeNode } = this,
            insertBefore = parentModel.children?.[insertIndex];

        parentModel.insertChild(childModels, insertBefore, false, {
            orderedBeforeNode : orderedBeforeNode ?? insertBefore?.previousSibling?.nextOrderedSibling
        });
    }
}
